<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue';
import { pluralize, type PresenceUser } from '@yana/shared';
import { useAuthStore } from '@/stores/auth';
import { useRealtime } from '@/composables/useRealtime';
import { presenceAction } from '@/lib/presenceText';
import { useObservation } from '@/composables/useObservation';
const UserActivityPanel = defineAsyncComponent(() => import('@/components/users/UserActivityPanel.vue'));
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';

/**
 * Кто ещё в системе и есть ли связь.
 *
 * Стояло это в панели над таблицей, справа, — рядом с фильтрами, видом
 * и кнопкой «Кандидат». Панель таблицы отвечает на вопрос «что я вижу и
 * как это отобрать»; «кто ещё работает» и «жива ли связь» — вопросы про
 * всё приложение. Отсюда переезд в общую шапку: панель перестала быть
 * свалкой из семи разнородных вещей.
 *
 * Виден показатель там, где живёт соединение, — то есть на таблице. На
 * остальных экранах сокет закрыт, и честнее не показывать ничего, чем
 * держать замерший снимок получасовой давности.
 *
 * Кружки — не украшение: по ним видно, что коллега правит ту же строку,
 * до того как выяснится, что правку кто-то затёр.
 */
const auth = useAuthStore();
const realtime = useRealtime();
const observation = useObservation();
const activityTarget = ref<PresenceUser | null>(null);
const showActivity = ref(false);

const others = computed(() => realtime.others.value);

/**
 * Кто именно в таблице — видит только администратор.
 *
 * Остальным сервер присылает присутствие обезличенным: имён и позиций
 * там попросту нет. Рекрутёру важно знать, что он в базе не один — тогда
 * чужая правка не станет неожиданностью; кто это и чем занят — дело
 * руководителя, а не повод следить друг за другом.
 */
const showsWho = computed(() => auth.can('audit.view'));

/** Чем занят коллега — по-человечески, а не «rowId: …». */
function presenceText(u: PresenceUser): string {
  return `${u.name} — ${presenceAction(u)}`;
}

function openActivity(user: PresenceUser): void {
  activityTarget.value = user;
  showActivity.value = true;
}
</script>

<template>
  <!--
    Ничего не показываем без соединения.

    Сокет живёт, только пока открыта таблица: на отчётах и в справочниках
    его закрывают. Значит и присутствие там — не «сейчас», а «когда-то»,
    и место ему в шапке лишь тогда, когда есть чем его обновлять.
  -->
  <div v-if="realtime.state.value !== 'offline'" class="flex shrink-0 items-center gap-1">
    <DropdownMenu v-if="others.length">
      <DropdownMenuTrigger as-child>
        <Button
          type="button"
          variant="ghost"
          class="hidden h-8 cursor-pointer items-center rounded-md px-1 transition-colors hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none sm:flex"
          :aria-label="`В таблице сейчас: ${others.length}`"
        >
          <span class="flex -space-x-1.5">
            <span
              v-for="u in others.slice(0, 4)"
              :key="u.userId"
              class="flex h-6 w-6 items-center justify-center rounded-full border-2 border-card text-[10px] font-bold text-white"
              :style="{ background: u.color }"
              :data-tip="showsWho ? presenceText(u) : 'В таблице работает кто-то ещё'"
              >{{ u.initials }}</span
            >
            <span
              v-if="others.length > 4"
              class="flex h-6 w-6 items-center justify-center rounded-full border-2 border-card bg-slate-400 text-[10px] font-bold text-white"
              >+{{ others.length - 4 }}</span
            >
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" class="w-72">
        <DropdownMenuLabel class="text-[11px] font-normal text-muted-foreground">
          {{ pluralize(others.length, 'человек', 'человека', 'человек') }} в таблице
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div v-if="!showsWho" class="px-2 py-1.5 text-[12px] text-muted-foreground">
          Кто именно — видно администратору. Правки коллег приходят сами.
        </div>

        <DropdownMenuSub
          v-for="u in showsWho ? others : []"
          :key="u.userId"
        >
          <DropdownMenuSubTrigger>
          <span
            class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
            :style="{ background: u.color }"
            >{{ u.initials }}</span
          >
          <span class="min-w-0 flex-1 truncate">
            {{ u.name }}
            <span class="text-muted-foreground">— {{ presenceAction(u) }}</span>
          </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem @select="observation.start(u)">Наблюдать в таблице</DropdownMenuItem>
            <DropdownMenuItem @select="openActivity(u)">Журнал действий</DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Подпись нужна, только когда связи нет: зелёная надпись
         «Синхронизация» занимала место постоянно и ничего не сообщала.
         Пока связь есть, остаётся точка — она подтверждает, что показатель
         живой, а не забытый. Пока соединения нет вовсе (другие разделы,
         сокет не поднимали) — не показываем ничего. -->
    <span
      class="flex h-8 items-center gap-1.5 px-1 text-[11px]"
      :class="realtime.isOnline.value ? 'text-muted-foreground' : 'text-amber-600'"
      :data-tip="
        realtime.isOnline.value
          ? 'Связь есть: правки других видны сразу'
          : 'Нет синхронизации: правки других появятся после обновления'
      "
    >
      <span
        class="size-2 rounded-full"
        :class="realtime.isOnline.value ? 'bg-green-500' : 'bg-amber-500'"
      />
      <span v-if="!realtime.isOnline.value" class="hidden whitespace-nowrap sm:inline">
        Без синхронизации
      </span>
    </span>
  </div>
  <UserActivityPanel v-if="showsWho && showActivity" v-model:open="showActivity" :user-id="activityTarget?.userId ?? null" :user-name="activityTarget?.name ?? ''" />
</template>
